import { useWorkspaceId } from "../../workspace/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BASE_API_URL } from "../../api/utils/useGovernmentAgencyHubAppQueryClient";
import { useHttpGovernmentAgencyHubAppHeaders } from "../../api/utils/useHttpGovernmentAgencyHubAppHeaders";
import { GovernmentAgencyType } from "../use-get-tax-submission-detail/TaxSubmissionDetailDTO";

interface SendTaxSubmissionRequest {
    id: string;
    uploadFlow: GovernmentAgencyType;
}

interface SendTaxSubmissionResult {
    succeded: { id: string }[];
    failed: { id: string; error: string }[];
}

export const useSendTaxSubmission = () => {
    const workspaceId = useWorkspaceId();
    const headers = useHttpGovernmentAgencyHubAppHeaders({ method: "PATCH" });
    const queryClient = useQueryClient();

    const mutation = useMutation<
        SendTaxSubmissionResult,
        Error,
        SendTaxSubmissionRequest[]
    >({
        mutationFn: async (taxSubmissions) => {
            const succeded: { id: string }[] = [];
            const failed: { id: string; error: string }[] = [];

            await Promise.allSettled(
                taxSubmissions.map(async (taxSubmission) => {
                    const response = await fetch(
                        `${BASE_API_URL}/tenants/${workspaceId}/send-awaiting-tax-submission/${taxSubmission.id}?${new URLSearchParams({ uploadFlow: taxSubmission.uploadFlow })}`,
                        {
                            headers,
                            method: "PATCH",
                        },
                    );
                    if (!response.ok) {
                        failed.push({
                            id: taxSubmission.id,
                            error: (await response.json())?.message,
                        });
                    } else {
                        succeded.push({ id: taxSubmission.id });
                    }
                }),
            );

            return { succeded, failed };
        },
        onSuccess: (result) => {
            if (result.succeded.length === 0) return;
            queryClient.invalidateQueries({
                queryKey: ["useGetTaxSubmissions"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
            queryClient.invalidateQueries({
                queryKey: ["useGetTaxSubmissionDetail"],
            });
        },
    });

    return mutation;
};
